import { useEffect, useState } from "react";
import { SHELL_TOOLS, type PlatformEvent, type RunDTO } from "../../../shared/types";
import { List, listRowCls } from "../../components/List";
import { ConversationSpend } from "../../components/ConversationSpend";
import { paidByWords } from "../../components/CostBreakdown";
import { ModelBadge } from "../../components/ModelBadge";
import { RunTree } from "../../components/RunTree";
import { TextLink } from "../../components/TextLink";
import { Badge, Button, Collapsible, cost, duration, Json, PageHeader, Panel, StatusBadge } from "../../components/ui";
import { get, post } from "../../lib/api";
import { realtime } from "../../lib/ws";

type InputPart = { chars: number; text: string | null };
type RunInput = {
  sessionId: string; resume: boolean; model: string; chars: number; tokens: number; overBudget: boolean;
  system: InputPart | null; sessionStart: InputPart | null; turnContext: InputPart | null; message: InputPart;
};
type Detail = { run: RunDTO; events: PlatformEvent[]; proxyCalls: any[]; input: RunInput | null };

const TYPE_COLORS: Record<string, string> = {
  "tool.call": "text-info",
  "tool.result": "text-muted",
  "assistant.message": "text-success",
  "delegation.created": "text-violet-600 dark:text-violet-400",
  "proxy.call": "text-fuchsia-600 dark:text-fuchsia-400",
  "run.failed": "text-danger",
  "run.cancelled": "text-warning",
};

function eventSummary(e: PlatformEvent): string {
  const p = e.payload ?? {};
  switch (e.type) {
    case "tool.call":
      return `${p.name} ${SHELL_TOOLS.includes(p.name) ? p.input?.command : JSON.stringify(p.input)}`.slice(0, 200);
    case "tool.result":
      return `${p.isError ? "ERROR " : ""}${String(p.content ?? "").slice(0, 200)}`;
    case "assistant.message":
      return String(p.text).slice(0, 200);
    case "delegation.created":
      return `→ ${p.agent}${p.scopeName ? ` · ${p.scopeName}` : p.scope ? ` · ${p.scope}` : ""} (${p.mode}): ${p.task}`.slice(0, 200);
    case "proxy.call":
      return `${p.connector} ${p.method} ${p.path} → ${p.status} (${p.durationMs}ms)`;
    case "message.created":
      return `${p.role}: ${String(p.content).slice(0, 160)}`;
    case "run.input":
      return `${p.resume ? "resumed" : "new"} session · ${p.chars} chars · ~${p.tokens} tokens of ours${p.overBudget ? " (over budget)" : ""}`;
    default:
      return p.agent ? `${p.agent}` : "";
  }
}

/** The parts of a run's input, in the order the agent read them. */
const INPUT_PARTS: [keyof Pick<RunInput, "system" | "sessionStart" | "turnContext" | "message">, string][] = [
  ["system", "system prompt"],
  ["turnContext", "the run block, or what changed since the last turn"],
  ["sessionStart", "what the new session picked up"],
  ["message", "the message"],
];

const n = (v: number) => v.toLocaleString("en-US");

export function AdminRunDetail({ runId }: { runId: string }) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [tree, setTree] = useState<RunDTO[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let off: (() => void) | undefined;
    let offConv: (() => void) | undefined;
    setDetail(null);
    get<Detail>(`/api/runs/${runId}`).then(
      (d) => {
        setDetail(d);
        const loadTree = () => get<RunDTO[]>(`/api/runs?conversationId=${d.run.conversationId}&limit=1000`).then((r) => setTree(r.reverse()), () => {});
        loadTree();
        const since = { [runId]: d.events.at(-1)?.seq ?? 0 };
        off = realtime.subscribe(
          `run:${runId}`,
          (e) => {
            if (e.type === "assistant.delta") return;
            setDetail((cur) => {
              if (!cur || cur.events.some((x) => x.seq === e.seq)) return cur;
              const run = e.type.startsWith("run.") ? { ...cur.run, status: e.payload.status ?? (e.type === "run.started" ? "running" : cur.run.status), ...(e.payload.result !== undefined ? { result: e.payload.result } : {}), ...(e.payload.costUsd !== undefined ? { costUsd: e.payload.costUsd } : {}) } : cur.run;
              return { ...cur, run: run as RunDTO, events: [...cur.events, e] };
            });
          },
          since,
        );
        offConv = realtime.subscribe(`conversation:${d.run.conversationId}`, (e) => {
          if (e.type.startsWith("run.")) loadTree();
        });
      },
      (err) => setError(err.message),
    );
    return () => {
      off?.();
      offConv?.();
    };
  }, [runId]);

  if (error) return <p className="p-6 text-sm text-danger">{error}</p>;
  if (!detail) return <p className="p-6 text-sm text-muted">Loading…</p>;
  const { run, events, proxyCalls, input } = detail;

  return (
    <div>
      <PageHeader
        title={`${run.agentName} run`}
        subtitle={
          <span className="flex flex-wrap items-center gap-3">
            <StatusBadge status={run.status} />
            <Badge>{run.trigger}</Badge>
            <Badge tone={run.principal === "agent" ? "blue" : "zinc"}>{run.principal === "agent" ? "own behalf" : "for a person"}</Badge>
            {/* A run from before 088 carries no model: the one its input was recorded with, never called a choice. */}
            <ModelBadge model={run.model ?? input?.model} defaultModel={run.model ? run.defaultModel : null} />
            <span>mode {run.mode}</span>
            <span>depth {run.depth}</span>
            <span>{duration(run.startedAt, run.finishedAt)}</span>
            <span title="This run's own cost at the model's list price">{cost(run.costUsd)}{paidByWords(run.paidBy) && ` · ${paidByWords(run.paidBy)}`}</span>
            {run.usage ? (
              <span title="Tokens this run: written to the prompt cache, read back from it, new input, and output">
                {n(run.usage.cacheWrite)} written · {n(run.usage.cacheRead)} read from cache · {n(run.usage.input)} new · {n(run.usage.output)} out
              </span>
            ) : (
              run.inputTokens !== null && <span>{run.inputTokens} in / {run.outputTokens} out tokens</span>
            )}
            <TextLink to={`/admin/conversations/${run.conversationId}`}>conversation</TextLink>
            <ConversationSpend conversationId={run.conversationId} />
          </span>
        }
        actions={
          (run.status === "running" || run.status === "queued") && (
            <Button variant="danger" onClick={() => post(`/api/runs/${run.id}/cancel`)}>
              Cancel run tree
            </Button>
          )
        }
      />
      <div className="grid grid-cols-1 gap-6 px-4 py-4 md:px-6 xl:grid-cols-[26rem_minmax(0,1fr)]">
        <div className="space-y-5">
          <Panel title="Delegation tree">
            <RunTree runs={tree} activeId={run.id} />
          </Panel>
          {input && (
            <List
              title="What it was handed"
              hint={`${input.chars.toLocaleString()} chars, ~${input.tokens.toLocaleString()} tokens of ours${input.overBudget ? ", over budget" : ""}`}
            >
              {INPUT_PARTS.map(([key, label]) => {
                const part = input[key];
                if (!part) return null;
                return (
                  <div key={key} className={listRowCls}>
                    <Collapsible summary={<span className="font-mono text-xs">{label} · {part.chars.toLocaleString()} chars</span>}>
                      <Json value={part.text ?? "(not kept)"} className="max-h-96 whitespace-pre-wrap" />
                    </Collapsible>
                  </div>
                );
              })}
            </List>
          )}
          <Panel title="Task">
            <Json value={run.task} className="max-h-60 whitespace-pre-wrap" />
          </Panel>
          {run.context && (
            <Panel title="Context handed over">
              <Json value={run.context} className="max-h-60 whitespace-pre-wrap" />
            </Panel>
          )}
          {run.result && (
            <Panel title="Result">
              <Json value={run.result} className="max-h-80 whitespace-pre-wrap" />
            </Panel>
          )}
          {run.error !== null && run.error !== undefined && (
            <Panel title={<span className="text-danger">Error</span>}>
              <Json value={run.error} />
            </Panel>
          )}
          <List title={`Proxy calls (${proxyCalls.length})`} empty="No proxy calls.">
            {proxyCalls.map((p) => (
              <div key={p.id} className={`flex gap-2 font-mono text-xs ${listRowCls}`}>
                <span>{p.method}</span>
                <span className="truncate">{p.path}</span>
                <span className={p.status >= 400 ? "text-danger" : "text-success"}>{p.status}</span>
                <span className="text-faint">{p.duration_ms}ms</span>
              </div>
            ))}
          </List>
        </div>
        <List title={`Event timeline (${events.length})`} empty="No events yet.">
          {events.map((e) => (
            <div key={e.seq} className={listRowCls}>
              <Collapsible
                summary={
                  <span className="flex min-w-0 gap-2 font-mono text-xs">
                    <span className="w-8 shrink-0 text-right text-faint">{e.seq}</span>
                    <span className="hidden shrink-0 text-faint sm:inline">{new Date(e.ts).toLocaleTimeString()}</span>
                    <span className={`shrink-0 sm:w-36 ${TYPE_COLORS[e.type] ?? "text-muted"}`}>{e.type}</span>
                    <span className="truncate text-muted">{eventSummary(e)}</span>
                  </span>
                }
              >
                <Json value={e.payload} className="max-h-96" />
              </Collapsible>
            </div>
          ))}
        </List>
      </div>
    </div>
  );
}
